/**
 * Snap the initial app-load month to the most recent month that actually
 * has data.
 *
 * Why this exists: `getDefaultMonth()` in `state/store.ts` returns the
 * UTC current month (`YYYY-MM` of `new Date().getUTC*()`). At ~5pm PT on
 * the last day of a month, UTC has already rolled into the next month —
 * so a West-Coast user would land on (e.g.) "June 2026" with zero
 * transactions even though all their May data is sitting one tap away.
 *
 * Contract:
 *   - `months` is the server-ordered DESC list returned by
 *     `GET /api/v1/transactions/months` (newest first; only months that
 *     have at least one active transaction).
 *   - `fallback` is the current store value (typically UTC current month).
 *   - Returns `fallback` unchanged if the server reports zero months
 *     (brand-new user — onboarding will route them elsewhere).
 *   - Returns `fallback` unchanged if `fallback` IS in the server list
 *     (user has data for this month — no snap needed).
 *   - Otherwise returns `months[0]` — the most recent month with data.
 *
 * Pure function. No side effects. Safe to call in any environment.
 */
export function resolveBestInitialMonth(
  months: readonly string[],
  fallback: string,
): string {
  if (months.length === 0) return fallback;
  if (months.includes(fallback)) return fallback;
  return months[0] ?? fallback;
}
